import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { Grid } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PublicIcon from '@mui/icons-material/Public';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';

const PLANS = [
  {
    name: 'Starter',
    price: '98,000',
    description: '小規模M&A案件に最適な基本プラン',
    features: [
      'チェックリスト管理（222項目）',
      '案件管理 3件まで',
      'PDFレポート出力',
      '日本法令データベース',
      'メールサポート',
    ],
    recommended: false,
    buttonText: '購入する',
  },
  {
    name: 'Professional',
    price: '498,000',
    description: '複数案件を扱う専門家・中堅企業向け',
    features: [
      'Starterの全機能',
      '案件管理 無制限',
      'AI対応策提案機能',
      '多国法令対応（日・米・英・EU）',
      'チーム共有・権限管理',
      '分析ダッシュボード',
      '優先サポート',
    ],
    recommended: true,
    buttonText: '購入する',
  },
  {
    name: 'Enterprise',
    price: null,
    description: '大規模組織・カスタマイズが必要な企業向け',
    features: [
      'Professionalの全機能',
      'テナント管理',
      'API連携',
      'カスタムチェック項目',
      'SSO対応',
      '専任サポート・導入支援',
      'SLA保証',
    ],
    recommended: false,
    buttonText: 'お問い合わせ',
  },
];

const FEATURES = [
  {
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 40 }} />,
    title: '222項目の包括チェックリスト',
    description: '法的・経済的・財政的・国際的リスクなど8カテゴリ222項目を網羅。M&A手法別にカスタマイズ可能。',
  },
  {
    icon: <GavelIcon sx={{ fontSize: 40 }} />,
    title: '法令データベース連携',
    description: 'e-Gov法令API・Congress.gov・legislation.gov.uk・EUR-Lexと連携し、最新法令を自動参照。',
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
    title: 'AI対応策提案',
    description: 'リスク項目に対してAIが具体的な対応策を提案。経験の浅い担当者でも抜け漏れを防止。',
  },
  {
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    title: 'レポート自動生成',
    description: 'チェック結果をPDFレポートとして出力。経営層への報告資料をワンクリックで作成。',
  },
  {
    icon: <PublicIcon sx={{ fontSize: 40 }} />,
    title: '多国対応',
    description: '日本・米国・英国・EU・中国の法制度に対応。クロスボーダーM&Aのリスク管理を支援。',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: 'チーム管理・権限制御',
    description: '案件ごとのアクセス権限設定で情報管理を徹底。複数チームでの並行作業に対応。',
  },
];

export function LandingPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FFFFFF' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 50%, #1976D2 100%)',
          color: '#FFFFFF',
          py: { xs: 8, md: 12 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}
            >
              M&A対応チェッカー
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 1, color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}
            >
              M&A包括分析システム
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 4, color: 'rgba(255,255,255,0.8)', maxWidth: 600, mx: 'auto' }}
            >
              222項目のリスクチェック、多国法令データベース、AI対応策提案を一つに。
              M&Aのデューデリジェンスを確実かつ効率的にサポートします。
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const el = document.getElementById('pricing');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  bgcolor: '#FFFFFF',
                  color: '#0D47A1',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                料金プランを見る
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  borderColor: '#FFFFFF',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                ログイン
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Developer Story Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: 2, bgcolor: '#FFFFFF' }}>
        <Container maxWidth="md">
          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 6,
              color: '#1A237E',
              fontSize: { xs: '1.75rem', md: '2.125rem' },
            }}
          >
            なぜこのプロダクトが生まれたか
          </Typography>

          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  component="img"
                  src="/images/supervisor.jpg"
                  alt="堀 晴美"
                  sx={{
                    width: { xs: 140, md: 180 },
                    height: { xs: 140, md: 180 },
                    borderRadius: '50%',
                    objectFit: 'cover',
                    objectPosition: 'top',
                    mx: 'auto',
                    border: '3px solid',
                    borderColor: '#0D47A1',
                    mb: 2,
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A237E' }}>
                  堀 晴美
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#455A64' }}>
                  元弁護士 / 元裁判官
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <SchoolIcon sx={{ fontSize: 16, color: '#455A64' }} />
                    <Typography variant="caption" sx={{ color: '#455A64' }}>東京大学法学部卒</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <SchoolIcon sx={{ fontSize: 16, color: '#455A64' }} />
                    <Typography variant="caption" sx={{ color: '#455A64' }}>オックスフォード大学 D.Phil.</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <PublicIcon sx={{ fontSize: 16, color: '#455A64' }} />
                    <Typography variant="caption" sx={{ color: '#455A64' }}>UNROD 非常勤理事</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Typography
                variant="body1"
                sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 2, color: '#1A237E' }}
              >
                裁判官として6年間、年間300件 ――
                合計1,800件超の案件を審理する中で、M&A関連の紛争を数多く目にしてきました。
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 2, color: '#1A237E', mt: 2 }}
              >
                その中には、「事前にリスクを体系的にチェックしていれば防げた紛争」が数え切れないほどありました。
                法的リスク、経済的リスク、国際的リスク ――
                どれか一つでも見落とせば、取引全体が損なわれる。
                それが法廷で繰り返される現実でした。
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 2,
                  color: '#1A237E',
                  mt: 2,
                  fontWeight: 600,
                }}
              >
                裁判官として見た「紛争の現場」と、オックスフォードで研究した国際商事法の知見。
                その両方をシステムに落とし込んだのが、M&A対応チェッカーです。
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 2, bgcolor: '#F5F7FA' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            sx={{ textAlign: 'center', fontWeight: 700, mb: 1, color: '#1A237E' }}
          >
            主な機能
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', mb: 6, color: '#455A64' }}
          >
            M&Aに必要なリスク管理をワンストップで提供
          </Typography>
          <Grid container spacing={4}>
            {FEATURES.map((feature) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #E0E0E0',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: '#0D47A1', mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1A237E' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#455A64' }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: { xs: 6, md: 10 }, px: 2 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            sx={{ textAlign: 'center', fontWeight: 700, mb: 1, color: '#1A237E' }}
          >
            料金プラン
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', mb: 1, color: '#455A64' }}
          >
            買い切り型 - 一度のお支払いでずっと使える
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', mb: 6, color: '#455A64' }}
          >
            月額料金なし。追加費用なし。
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {PLANS.map((plan) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan.name}>
                <Card
                  elevation={plan.recommended ? 8 : 1}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    border: plan.recommended ? '2px solid #0D47A1' : '1px solid #E0E0E0',
                    borderRadius: 3,
                    overflow: 'visible',
                  }}
                >
                  {plan.recommended && (
                    <Chip
                      icon={<StarIcon sx={{ fontSize: 16 }} />}
                      label="おすすめ"
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 700,
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, mb: 1, color: '#1A237E' }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#455A64', mb: 3 }}>
                      {plan.description}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {plan.price ? (
                        <>
                          <Typography
                            variant="h3"
                            component="span"
                            sx={{ fontWeight: 800, color: '#0D47A1' }}
                          >
                            &yen;{plan.price}
                          </Typography>
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ color: '#455A64', ml: 1 }}
                          >
                            (税別)
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, color: '#0D47A1' }}
                        >
                          要お問い合わせ
                        </Typography>
                      )}
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    <Stack spacing={1.5} sx={{ flexGrow: 1, mb: 3 }}>
                      {plan.features.map((feature) => (
                        <Stack direction="row" spacing={1} alignItems="flex-start" key={feature}>
                          <CheckCircleOutlineIcon
                            sx={{ fontSize: 18, color: '#2E7D32', mt: 0.3, flexShrink: 0 }}
                          />
                          <Typography variant="body2" sx={{ color: '#455A64' }}>
                            {feature}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                    <Button
                      variant={plan.recommended ? 'contained' : 'outlined'}
                      fullWidth
                      size="large"
                      sx={{ py: 1.5, fontWeight: 700 }}
                      onClick={() => navigate('/login')}
                    >
                      {plan.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
          color: '#FFFFFF',
          py: { xs: 6, md: 8 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              M&Aリスク管理を、もっとシンプルに
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.8)' }}>
              導入に関するご質問はお気軽にお問い合わせください
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: '#FFFFFF',
                color: '#0D47A1',
                fontWeight: 700,
                px: 6,
                py: 1.5,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              今すぐ始める
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, px: 2, bgcolor: '#F5F7FA' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ textAlign: 'center', color: '#455A64' }}>
            &copy; {new Date().getFullYear()} M&A対応チェッカー. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
